import { Navigate, useRoutes } from "react-router-dom";
// auth
import AuthGuard from "../auth/AuthGuard";
import GuestGuard from "../auth/GuestGuard";
// layouts
import CompactLayout from "../layouts/compact";
import DashboardLayout from "../layouts/dashboard";
// config
import { PATH_AFTER_LOGIN } from "../config";
//
import {
  Page404,
  LoginPage,
  LoginVerifyPage,
  ResetPassword,
  SignUpPage,
  SignUpEmailVerify,
  SignUpMobileVerify,
  BusinessVerification,
  BusinessDocuments,
  DirectorAndPromoter,
  DirectorDocuments,
  ApplicationReview,
  BankSelection,
  BankSelectionSteps,
  BankOSV,
  BankAccountOpen,
  BankAccountSetUp,
  Home,
  Reports,
  AssuredPayBilling,
  UploadNewInvoice,
  PendingPayables,
  PayableDetail,
  PendingReceivables,
  SetNewPassword,
  NewConnections,
  MakingPayment,
  SuccessPayment,
  TermsAndCondition,
  MyPayables,
  MyReceivables,
  AccessControl,
  BankAccounts,
  WithdrawalBankAccount,
  ReceivableDetails,
  MyReceivableDetails,
  MyPayablesDetails,
  PendingPayablesDetails,
  Chat,
  Profile,
  Ticket,
  AffiliateDashboard,
  Invitation,
  AffiliateBankAccounts,
  Commission,
  SalesDashboard,
  SalesList,
  SalesProfile,
  CreateTicket,
  LeadAffiliate,
  LeadSales,
} from "./elements";
import GuestLayout from "../layouts/Guest";
import KYCLayout from "../layouts/Kyc";
import KycGuard from "../auth/KycGuard";
import BankGuard from "../auth/BankGuard";
import AffiliateDashboardLayout from "../layouts/affiliate-dashboard";
import AffiliateAuthGuard from "../auth/AffiliateAuthGuard";
import SalesDashboardLayout from "../layouts/sales-dashboard";
import SalesUserGuard from "../auth/SalesUserGuard";
import Team from "../pages/sales/Team";
import Performance from "../pages/sales/Performance";

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      path: "/",
      element: (
        <GuestGuard>
          <GuestLayout />
        </GuestGuard>
      ),
      children: [
        { element: <Navigate to={"/login"} replace />, index: true },
        { path: "login", element: <LoginPage /> },
        { path: "login-verify", element: <LoginVerifyPage /> },
        { path: "reset", element: <ResetPassword /> },
        { path: "sign-up", element: <SignUpPage /> },
        { path: "sign-up-mobile-verify", element: <SignUpMobileVerify /> },
        { path: "sign-up-email-verify", element: <SignUpEmailVerify /> },
      ],
    },
    {
      path: "/kyc",
      element: (
        <KycGuard>
          <KYCLayout />
        </KycGuard>
      ),
      children: [
        {
          element: <Navigate to={"/kyc/businessVerification"} replace />,
          index: true,
        },
        { path: "businessVerification", element: <BusinessVerification /> },
        { path: "businessDocuments", element: <BusinessDocuments /> },
        { path: "directorAndPromoter", element: <DirectorAndPromoter /> },
        { path: "DirectorDocuments", element: <DirectorDocuments /> },
        { path: "ApplicationReview", element: <ApplicationReview /> },
      ],
    },
    {
      path: "/bank",
      element: (
        <BankGuard>
          <KYCLayout />
        </BankGuard>
      ),
      children: [
        {
          element: <Navigate to={"/bank/BankSelection"} replace />,
          index: true,
        },
        { path: "BankSelection", element: <BankSelection /> },
        { path: "BankSelectionSteps", element: <BankSelectionSteps /> },
        { path: "BankOSV", element: <BankOSV /> },
        { path: "BankAccountOpen", element: <BankAccountOpen /> },
        { path: "BankAccountSetUp", element: <BankAccountSetUp /> },
        { path: "WithdrawalBankAccount", element: <WithdrawalBankAccount /> },
      ],
    },
    {
      path: "/auth",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: "home",
          children: [
            { element: <Navigate to={"/auth/home"} replace />, index: true },
            { path: "dashboard", element: <Home /> },
            { path: "uploadNewinvoice", element: <UploadNewInvoice /> },
            {
              path: "myPayables",
              children: [
                {
                  element: (
                    <Navigate to={"/auth/home/myPayables/list"} replace />
                  ),
                  index: true,
                },
                { path: "list", element: <MyPayables /> },
                { path: "view", element: <MyPayablesDetails /> },
              ],
            },
            {
              path: "pendingPayables",
              children: [
                {
                  element: (
                    <Navigate to={"/auth/home/pendingPayables/list"} replace />
                  ),
                  index: true,
                },
                { path: "list", element: <PendingPayables /> },
                { path: "details", element: <PendingPayablesDetails /> },
                { path: "view/:id", element: <PayableDetail /> },
                { path: "view/:id/MakePayment", element: <MakingPayment /> },
                {
                  path: "view/:id/SuccessPayment",
                  element: <SuccessPayment />,
                },
              ],
            },
            {
              path: "myReceivables",
              children: [
                {
                  element: (
                    <Navigate to={"/auth/home/myReceivables/list"} replace />
                  ),
                  index: true,
                },
                { path: "list", element: <MyReceivables /> },
                { path: "view", element: <MyReceivableDetails /> },
              ],
            },
            {
              path: "pendingReceivables",
              children: [
                {
                  element: (
                    <Navigate
                      to={"/auth/home/pendingReceivables/list"}
                      replace
                    />
                  ),
                  index: true,
                },
                { path: "list", element: <PendingReceivables /> },
                { path: "view", element: <ReceivableDetails /> },
              ],
            },
            { path: "newConnections", element: <NewConnections /> },
          ],
        },
        { path: "AccessControl", element: <AccessControl /> },
        { path: "reports", element: <Reports /> },
        { path: "assuredPayBilling", element: <AssuredPayBilling /> },
        { path: "BankAccounts", element: <BankAccounts /> },
        { path: "Chat", element: <Chat /> },
        { path: "Profile", element: <Profile /> },
        {
          path: "support",
          children: [
            { element: <Ticket />, index: true },
            { path: "create_ticket", element: <CreateTicket /> },
          ],
        },
      ],
    },

    {
      path: "/affiliate",
      element: (
        <AffiliateAuthGuard>
          <AffiliateDashboardLayout />
        </AffiliateAuthGuard>
      ),
      children: [
        {
          element: <Navigate to={"/affiliate/dashboard"} replace />,
          index: true,
        },
        { path: "dashboard", element: <AffiliateDashboard /> },
        { path: "invitation", element: <Invitation /> },
        { path: "bank-accounts", element: <AffiliateBankAccounts /> },
        { path: "Commission", element: <Commission /> },
        { path: "Leads", element: <LeadAffiliate /> },
      ],
    },

    {
      path: "/sales",
      element: (
        <SalesUserGuard>
          <SalesDashboardLayout />
        </SalesUserGuard>
      ),
      children: [
        {
          element: <Navigate to={"/sales/dashboard"} replace />,
          index: true,
        },
        { path: "dashboard", element: <SalesDashboard /> },
        { path: "InviteAffiliateUser", element: <SalesList /> },
        { path: "team", element: <Team /> },
        { path: "performance", element: <Performance /> },
        { path: "profile", element: <SalesProfile /> },
        { path: "Leads", element: <LeadSales /> },
      ],
    },

    {
      element: <CompactLayout />,
      children: [
        { path: "reset-password", element: <SetNewPassword /> },
        { path: "TermsAndCondition", element: <TermsAndCondition /> },
        { path: "404", element: <Page404 /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}
